import { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  Box,
  Avatar,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "@mui/icons-material/Logout";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { useSelector, useDispatch } from "react-redux";
import { signOut } from "../../utils/authSession";
import { updateUser } from "../../store/auth.slice";
import { apiUpdateMe } from "../../graphQL/user";
import { apiUploadFileToS3 } from "../../graphQL/file";
import { v4 as uuid_v4 } from "../../libs/uuid";
import config from "../../config";
import AppIconButton from "./AppIconButton";

const avatarUrlOf = (av) => {
  if (!av) return "";
  if (typeof av === "string") return av;
  if (av.url) return av.url;
  if (av.signedUrl) return av.signedUrl;
  if (av.base && av.dir && av.name) {
    return `${String(av.base).replace(/\/+$/, "")}/${String(av.dir).replace(/^\/+|\/+$/g, "")}/${av.name}`;
  }
  return "";
};

const splitName = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: "", last: "" };
  return { first: parts[0], last: parts.slice(1).join(" ") };
};

const ProfilePopup = ({ open, onClose }) => {
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const fileRef = useRef(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [avatar, setAvatar] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Hydrate form each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    const { first, last } = splitName(user?.name);
    setFirstName(first);
    setLastName(last);
    setAvatar(user?.avatar || null);
    setError("");
  }, [open, user]);

  const avatarUrl = useMemo(() => avatarUrlOf(avatar), [avatar]);
  const initials = useMemo(() => {
    const f = (firstName || "").trim()[0] || "";
    const l = (lastName || "").trim()[0] || "";
    return (f + l).toUpperCase() || String(user?.email || "?")[0].toUpperCase();
  }, [firstName, lastName, user]);

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const domainKey = String(user?.domain || "myrighthand").trim() || "myrighthand";
      const ext = file.name && file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "png";
      const name = `${uuid_v4()}.${ext}`;
      const dir = `${domainKey}/avatars`;
      const key = `${dir}/${name}`;
      await apiUploadFileToS3(key, file, { contentType: file.type, isPublic: true });
      const base = String(config?.URL?.REACT_APP_PUBLIC_ASSETS_BASE_URL || "https://assets.yourrighthand.ai").replace(/\/+$/, "");
      const url = `${base}/${key}`;
      setAvatar({ url, base, dir, name });
    } catch (err) {
      console.error("Avatar upload failed:", err);
      setError(err?.message || "Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const name = `${firstName.trim()} ${lastName.trim()}`.trim() || user?.name || "";
      const patch = { name };
      if (avatar) patch.avatar = avatar;
      const updated = await apiUpdateMe(patch);
      dispatch(updateUser({ name: updated?.name || name, avatar: updated?.avatar || avatar }));
      onClose();
    } catch (err) {
      console.error("Profile save failed:", err);
      setError(err?.message || "Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5 }}>
        Profile
        <AppIconButton onClick={onClose} aria-label="Close profile dialog">
          <CloseIcon fontSize="small" />
        </AppIconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {/* Avatar */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
            <Box sx={{ position: "relative" }}>
              <Avatar src={avatarUrl || undefined} sx={{ width: 84, height: 84, fontSize: 28, bgcolor: "#3F51B5" }}>
                {!avatarUrl ? initials : null}
              </Avatar>
              <IconButton
                size="small"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                sx={{
                  position: "absolute", bottom: -4, right: -4,
                  bgcolor: "#3F51B5", color: "#fff", width: 28, height: 28,
                  "&:hover": { bgcolor: "#3949AB" },
                }}
                aria-label="Upload photo"
              >
                {uploading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <PhotoCameraIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Box>
            <Button size="small" onClick={() => fileRef.current?.click()} disabled={uploading} sx={{ textTransform: "none" }}>
              {avatarUrl ? "Change photo" : "Add photo"}
            </Button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickImage} />
          </Box>

          {/* Name */}
          <Stack direction="row" spacing={1.5}>
            <TextField label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} fullWidth size="small" />
            <TextField label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} fullWidth size="small" />
          </Stack>

          {user?.email && (
            <TextField label="Email" value={user.email} fullWidth size="small" disabled />
          )}

          {error && <Alert severity="error">{error}</Alert>}

          <List disablePadding sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
            <ListItemButton onClick={signOut}>
              <LogoutIcon fontSize="small" sx={{ mr: 1.25, color: "text.secondary" }} />
              <ListItemText primary="Logout" secondary="Sign out to access Forgot password" />
            </ListItemButton>
          </List>
          <Typography variant="caption" color="text.secondary">
            To change your password: log out, then click Forgot password on the login screen.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || uploading} sx={{ textTransform: "none" }}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProfilePopup;
